import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { Linkedin, Twitter, Edit3, Loader2, Plus, Trash2, X } from 'lucide-react';

export default function About() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'platform_admin' || user.role === 'business_admin');

  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | {id, ...}
  const [form, setForm] = useState({ name: '', title: '', bio: '', photo_url: '', linkedin_url: '', twitter_url: '', is_founder: true, active: true, order: 0 });
  const [saving, setSaving] = useState(false);

  const loadTeam = async () => {
    try {
      const { data } = await api.get('/api/marketing/team');
      setTeam(data.team || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadTeam(); }, []);

  const openEdit = (member) => {
    setForm({ ...member });
    setEditing(member);
  };
  const openNew = () => {
    setForm({ name: '', title: '', bio: '', photo_url: '', linkedin_url: '', twitter_url: '', is_founder: false, active: true, order: team.length });
    setEditing('new');
  };
  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/api/marketing/team', form);
      } else {
        await api.put(`/api/marketing/team/${editing.id}`, form);
      }
      toast.success('Saved');
      setEditing(null);
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    try {
      await api.delete(`/api/marketing/team/${id}`);
      toast.success('Deleted');
      loadTeam();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <PublicNav />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full bg-[#D4A843]/15 text-[#D4A843] uppercase tracking-wider mb-5">About EIGOO Inc.</span>
        <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">Built from the island of <span className="text-[#D4A843]">Guam</span>, for the operators everyone else ignores.</h1>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">We noticed small manufacturers, medical practices, and service businesses getting priced out of real cybersecurity. So we built the platform we wished existed — powerful, honest, and actually affordable.</p>
      </section>

      {/* Mission + Values */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { title: 'Our Mission', body: 'Make enterprise-grade IT and cybersecurity genuinely accessible to small and mid-sized operators. No consulting markups, no locked platforms, no BS.' },
            { title: 'Our Values', body: 'Radical transparency on pricing. Pragmatic security over checkbox compliance. Respect for the operators who actually keep America running.' },
            { title: 'Our Promise', body: 'If we can\'t make you measurably safer or more efficient in 90 days, we refund the difference. No long contracts, no fine print.' },
          ].map((card) => (
            <div key={card.title} className="bg-gradient-to-br from-[#111] to-[#0A0A0A] border border-gray-800 rounded-2xl p-6 hover:border-[#D4A843]/40 transition-colors">
              <h3 className="text-[#D4A843] text-sm font-bold uppercase tracking-wider mb-3">{card.title}</h3>
              <p className="text-sm text-gray-300 leading-relaxed">{card.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold">The team</h2>
          {isAdmin && (
            <Button size="sm" onClick={openNew} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs h-9" data-testid="about-add-member">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
            </Button>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {team.map((m) => (
            <div key={m.id} className="bg-[#111] border border-gray-800 rounded-2xl p-6 relative group" data-testid={`team-member-${m.id}`}>
              {isAdmin && (
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button onClick={() => openEdit(m)} className="p-1.5 bg-black/50 hover:bg-[#D4A843] hover:text-[#0A0A0A] rounded" data-testid={`team-edit-${m.id}`}><Edit3 className="w-3 h-3" /></button>
                  <button onClick={() => remove(m.id)} className="p-1.5 bg-black/50 hover:bg-red-500 rounded"><Trash2 className="w-3 h-3" /></button>
                </div>
              )}
              {m.photo_url ? (
                <img src={m.photo_url} alt={m.name} className="w-20 h-20 rounded-full object-cover mb-4 border-2 border-[#D4A843]/30" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4A843]/20 to-[#7C3AED]/20 border-2 border-[#D4A843]/30 flex items-center justify-center text-xl font-bold text-[#D4A843] mb-4">
                  {m.name?.split(' ').map(w => w[0]).slice(0, 2).join('')}
                </div>
              )}
              <h3 className="text-lg font-bold text-white">{m.name}</h3>
              <p className="text-xs text-[#D4A843] uppercase tracking-wide mt-0.5">{m.title}</p>
              {m.is_founder && <span className="inline-block mt-2 text-[9px] font-bold px-2 py-0.5 rounded bg-[#D4A843]/15 text-[#D4A843] uppercase tracking-wide">Founder</span>}
              <p className="text-xs text-gray-400 leading-relaxed mt-3 whitespace-pre-wrap">{m.bio}</p>
              <div className="flex gap-2 mt-4">
                {m.linkedin_url && <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#0A66C2]"><Linkedin className="w-4 h-4" /></a>}
                {m.twitter_url && <a href={m.twitter_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-sky-400"><Twitter className="w-4 h-4" /></a>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Admin edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#111] border border-gray-800 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h3 className="font-bold">{editing === 'new' ? 'New team member' : 'Edit team member'}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><Label className="text-xs text-gray-400">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" /></div>
              <div><Label className="text-xs text-gray-400">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" placeholder="e.g., Founder & CEO" /></div>
              <div><Label className="text-xs text-gray-400">Bio</Label><textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="mt-1 w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white min-h-[120px]" /></div>
              <div><Label className="text-xs text-gray-400">Photo URL</Label><Input value={form.photo_url} onChange={e => setForm({...form, photo_url: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" placeholder="https://..." /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-400">LinkedIn URL</Label><Input value={form.linkedin_url} onChange={e => setForm({...form, linkedin_url: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" /></div>
                <div><Label className="text-xs text-gray-400">Twitter URL</Label><Input value={form.twitter_url} onChange={e => setForm({...form, twitter_url: e.target.value})} className="mt-1 bg-[#0A0A0A] border-gray-700 text-white" /></div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_founder} onChange={e => setForm({...form, is_founder: e.target.checked})} /> <span className="text-xs text-gray-300">Founder</span></label>
              <Button onClick={save} disabled={saving} className="w-full bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="team-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
